import {
    Delegate,
    Sso,
} from '../../../../api/types/properties'
import {
    FingerPrintIcon,
    LockClosedIconSolid,
} from '../../../Icon'
import Attribute from '../../InfoMessage/Attribute'
import {MessageProps} from '../../types'
import React from 'react'
import {styled} from '../../../../stitches/config'
import {useTranslation} from 'react-i18next'

interface SSOMessageProps extends MessageProps { isMe: boolean}

const StyledMessage = styled('div', {flexDirection: 'column'})

const StyledTable = styled('table', {
    marginLeft: '30px',
    marginTop: '8px',
})

const StyledCaption = styled('caption', {
    textAlign: 'left',
    variants: {type: {test: {color: '$error'}}},
    whiteSpace: 'nowrap',
})

const StyledTH = styled('th', {})

const StyledHeader = styled('div', {
    alignItems: 'center',
    color: '#768FAB',
    display: 'flex',
    fontSize: '14px',
    fontWeight: 'normal',
})

const StyledIcon = styled('div', {
    display: 'flex',
    flex: 'none',
    height: '12px',
    marginLeft: '4px',
    width: '12px',
})

const MetadataSSOMessage = (props: SSOMessageProps): React.ReactElement => {
    const {
        data,
        isMe,
        message_user_name,
    } = props

    const {t} = useTranslation()

    const getSsoData = (sso: Sso): React.ReactElement => (
        <StyledTable>
            {!!sso.Test &&
                <StyledCaption type='test'>{t('Test')}</StyledCaption>}
            <tbody>
                {!!sso['Personal identity code'] &&
                    <tr>
                        <StyledTH><StyledHeader>{t('SSN')}<StyledIcon><LockClosedIconSolid /></StyledIcon></StyledHeader></StyledTH>
                        <td>{sso['Personal identity code']}</td>
                    </tr>}
                {!!sso['First name'] &&
                    <tr>
                        <StyledTH><StyledHeader>{t('First name')}<StyledIcon><LockClosedIconSolid /></StyledIcon></StyledHeader></StyledTH>
                        <td>{sso['First name']}</td>
                    </tr>}
                {!!sso['Last name'] &&
                    <tr>
                        <StyledTH><StyledHeader>{t('Last name')}<StyledIcon><LockClosedIconSolid /></StyledIcon></StyledHeader></StyledTH>
                        <td>{sso['Last name']}</td>
                    </tr>}
                {!!sso.Mail &&
                    <tr>
                        <StyledTH><StyledHeader>{t('Email address')}<StyledIcon><LockClosedIconSolid /></StyledIcon></StyledHeader></StyledTH>
                        <td>{sso.Mail}</td>
                    </tr>}
            </tbody>
        </StyledTable>
    )

    const getSsoDelegate = (delegate: Delegate): React.ReactElement => (
        <StyledTable>
            <StyledCaption>{t('Acting as delegate for')}</StyledCaption>
            <tbody>
                {!!delegate['Personal identity code'] &&
                    <tr>
                        <StyledTH><StyledHeader>{t('SSN')}<StyledIcon><LockClosedIconSolid /></StyledIcon></StyledHeader></StyledTH>
                        <td>{delegate['Personal identity code']}</td>
                    </tr>}
                {!!delegate.Name &&
                    <tr>
                        <StyledTH><StyledHeader>{t('Name')}<StyledIcon><LockClosedIconSolid /></StyledIcon></StyledHeader></StyledTH>
                        <td>{delegate.Name}</td>
                    </tr>}
            </tbody>
        </StyledTable>
    )

    const getMessage = (sso: Sso): React.ReactElement | null => {
        const userName = message_user_name || t('Unnamed')

        switch (sso.Authentication) {
            case 'canceled':
                return (
                    <Attribute
                        icon={FingerPrintIcon}
                        text={isMe ? t('You canceled request to authenticate') : t('{{userName}} canceled request to authenticate', {userName})}
                        type='error'
                    />
                )
            case 'declined':
                return (
                    <Attribute
                        icon={FingerPrintIcon}
                        text={isMe ? t('You declined request to authentication') : t('{{userName}} declined request to authenticate', {userName})}
                        type='error'
                    />
                )
            case 'failed':
                return (
                    <Attribute
                        icon={FingerPrintIcon}
                        text={isMe ? t('Your authentication failed') : t('{{userName}} authentication failed', {userName})}
                        type='error'
                    />
                )
            case 'in progress':
                return (
                    <Attribute
                        icon={FingerPrintIcon}
                        text={isMe ? t('You are authenticating') : t('{{userName}} is authenticating', {userName})}
                    />
                )
            case 'requested': {
                return (
                    <Attribute
                        icon={FingerPrintIcon}
                        text={isMe ? t('You requested all participants to authenticate') : t('You were requested to authenticate')}
                        type='warning'
                    />
                )
            }
            case 'successful':
            default:
                return (
                    <>
                        <Attribute
                            icon={FingerPrintIcon}
                            text={isMe ? t('You authenticated successfully') : t('{{userName}} authenticated successfully', {userName})}
                            type='success'
                        />
                        {getSsoData(sso)}
                        {!!sso.Delegate && sso.Delegate.map((delegate) => (
                            <div key={delegate['Personal identity code']}>
                                {getSsoDelegate(delegate)}
                            </div>
                        ))}
                    </>
                )
        }
    }

    return (
        <StyledMessage>{getMessage(data?.sso as Sso)}</StyledMessage>
    )
}

export default MetadataSSOMessage
